export default {
  computed: {
    canPolicyEdit() {
      return this.$_can('policy-edit');
    },

    canFirmView() {
      return this.$_can('firm-view');
    },

    showActionsColumn() {
      return this.canPolicyEdit || this.canFirmView;
    },

    searchCountLabel() {
      return `${this.filteredRows.length}/${this.rows.length}`;
    },

    filteredRows() {
      const term = this.normalizeSearchTerm(this.filter);
      if (!term) return this.rows;
      return this.rows.filter((row) => {
        if (!row._searchBlob) row._searchBlob = this.buildSearchBlob(row, row);
        return row._searchBlob.includes(term);
      });
    },

    displayHeaders() {
      const headers = this.headers.map((header) => ({ ...header }));
      const receivableTabs = new Set(['ar', 'car', 'canar']);
      const payableTabs = new Set(['ap', 'cap', 'canap', 'lpp']);
      const payeeHeader = headers.find((h) => h.value === 'payor');
      const isPaymentsScreen = this.$options && this.$options.name === 'Payments';
      if (payeeHeader && isPaymentsScreen) {
        if (receivableTabs.has(this.activeTab)) payeeHeader.text = 'Payor';
        else if (payableTabs.has(this.activeTab)) payeeHeader.text = 'Payee';
      }
      return this.showActionsColumn ? headers : headers.filter((h) => h.value !== 'actions');
    }
  },

  methods: {
    formatCurrency(value) {
      const num = Number(value) || 0;
      const rounded = Math.round(num * 100) / 100;
      const isNegativeZero = rounded === 0 && (num < 0 || Object.is(num, -0));
      const sign = (rounded < 0 || isNegativeZero) ? '-' : '';
      const absCurrency = Math.abs(rounded).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
      return `${sign}$${absCurrency}`;
    },

    formatPrintedAt(dateValue) {
      const d = new Date(dateValue);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hour24 = d.getHours();
      const minute = String(d.getMinutes()).padStart(2, '0');
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      const hour12 = hour24 % 12 || 12;
      return `${mm}-${dd}-${yyyy} at ${hour12}:${minute} ${ampm}`;
    },

    formatReportDate(dateValue) {
      if (!dateValue) return '';
      const value = String(dateValue).trim();
      if (!value) return '';
      if (/^\d{2}-\d{2}-\d{4}$/.test(value)) return value;

      const ymdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (ymdMatch) return `${ymdMatch[2]}-${ymdMatch[3]}-${ymdMatch[1]}`;

      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      return this.$_date(parsed);
    },

    normalizeSearchTerm(value) {
      return String(value === null || value === undefined ? '' : value).toLowerCase().replace(/\s+/g, ' ').trim();
    },

    htmlToPlainText(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html || '';
      return (tmp.textContent || '').trim();
    },

    escapeHtml(value) {
      const text = String(value === null || value === undefined ? '' : value);
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    prepareNoteValues(rawNote) {
      const normalized = String(rawNote || '').replace(/\uFFFD/g, '-');
      if (!normalized) return { note: '', noteHtml: '' };

      // Fast path for plain text notes (most common): avoid DOM sanitizer.
      if (!normalized.includes('<')) {
        return {
          note: normalized.trim(),
          noteHtml: this.escapeHtml(normalized)
        };
      }

      const noteHtml = this.sanitizeNoteHtml(normalized);
      return {
        note: this.htmlToPlainText(noteHtml),
        noteHtml
      };
    },

    sanitizeNoteHtml(rawHtml) {
      if (!rawHtml) return '';
      const container = document.createElement('div');
      container.innerHTML = rawHtml;
      this.sanitizeNoteNodeTree(container);
      return container.innerHTML;
    },

    sanitizeNoteNodeTree(root) {
      const allowedTags = new Set(['B', 'FONT']);
      const nodes = Array.from(root.querySelectorAll('*'));

      nodes.forEach((node) => {
        const tag = node.tagName;
        if (!allowedTags.has(tag)) {
          const textNode = document.createTextNode(node.textContent || '');
          if (node.parentNode) node.parentNode.replaceChild(textNode, node);
          return;
        }

        const attrs = Array.from(node.attributes || []);
        attrs.forEach((attr) => {
          const name = attr.name.toLowerCase();
          if (tag === 'FONT' && name === 'color') {
            const normalized = this.normalizeColorValue(attr.value);
            if (normalized) node.setAttribute('color', normalized);
            else node.removeAttribute(attr.name);
            return;
          }
          node.removeAttribute(attr.name);
        });
      });
    },

    normalizeColorValue(value) {
      const v = String(value || '').trim();
      if (!v) return '';
      if (/^#[0-9a-fA-F]{3,8}$/.test(v)) return v;
      if (/^(?:rgb|hsl)a?\(\s*[-\d.%\s,]+\)$/.test(v)) return v;
      if (/^[a-zA-Z]+$/.test(v)) return v.toLowerCase();
      return '';
    },

    valuesEqualForDisplay(left, right) {
      const l = Number(left);
      const r = Number(right);
      if (Number.isFinite(l) && Number.isFinite(r)) return Math.abs(l - r) < 0.005;
      return String(left) === String(right);
    },

    formatFilteredVsOverall(filteredValue, overallValue, formatter = (value) => `${value}`) {
      if (this.valuesEqualForDisplay(filteredValue, overallValue)) return formatter(overallValue);
      return `${formatter(filteredValue)} of ${formatter(overallValue)}`;
    },

    buildDateSortValue(dateValue) {
      if (!dateValue) return Number.MAX_SAFE_INTEGER;
      const value = String(dateValue).trim();
      if (!value) return Number.MAX_SAFE_INTEGER;

      const ymdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (ymdMatch) return Date.UTC(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]));

      const mdyMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (mdyMatch) return Date.UTC(Number(mdyMatch[3]), Number(mdyMatch[1]) - 1, Number(mdyMatch[2]));

      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? Number.MAX_SAFE_INTEGER : parsed.getTime();
    },

    buildSearchBlob(row, rawItem) {
      const amountDisplay = this.formatCurrency(row.amountValue);
      const amountRaw = Number(row.amountValue || 0).toFixed(2);
      const amountRawNoCommas = amountDisplay.replace(/[$,\s]/g, '');
      const inceptRaw = rawItem && (rawItem._incept || rawItem.incept || '');
      const dueRaw = rawItem && (rawItem._due || rawItem.due || '');
      return this.normalizeSearchTerm([
        row.payor,
        row.insured,
        row.incept,
        inceptRaw,
        row.producer,
        row.type,
        row.acct,
        row.ref,
        row.note,
        row.dueDate,
        dueRaw,
        row.ageText,
        row.ageSortDays,
        amountDisplay,
        amountRaw,
        amountRawNoCommas
      ].join(' | '));
    }
  }
};
